import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import Container from 'reactstrap/lib/Container';

// Models
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { DealerInfoModel, DealerInfoEntities } from 'client/data/models/dealer-info';
import { DealerInventoryModel } from 'client/data/models/dealer-inventory';
// Tracking
import { TrackingMap } from 'client/tracking/maps/dealer-details-page/index';
import { TrackingHandler } from 'client/tracking/handler';
import { EngagementHandler } from 'client/engagement-handlers/index';
import { DealerInternalReferrerEngagementHandler } from 'client/engagement-handlers/dealer-internal-referrer-engagement-handler/dealer-internal-referrer-engagement-handler';
// Utils
import { makeDDPCityStateName } from 'site-modules/shared/utils/nice-name';
import { getDealerPageBreadcrumbs } from 'client/site-modules/dealerships/utils/breadcrumbs';
import { encodeAndLowercaseDealerLogicalName } from 'client/site-modules/dealerships/utils/logical-name';
// Constants
import { DEALER_PROPS } from 'client/constants/dealer-reviews';
// Components
import { DealerPhotosCarouselUI } from 'site-modules/dealerships/components/dealer-photos-carousel/dealer-photos-carousel';
import { DealerInventoryCarousel } from 'site-modules/dealerships/components/dealer-inventory-carousel/dealer-inventory-carousel';
import { DealerReviews } from 'site-modules/dealerships/components/dealer-reviews/dealer-reviews';
import { DealerVideos } from 'site-modules/dealerships/components/dealer-videos/dealer-videos';
import { DealerAboutInfoUI } from 'site-modules/dealerships/components/dealer-about-info/dealer-about-info';
import { DealerDetails } from 'site-modules/dealerships/components/dealer-details/dealer-details';
import { DealerSeoLinks } from 'site-modules/dealerships/components/dealer-seo-links/dealer-seo-links';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { IcoWidget } from 'client/site-modules/shared/components/dealer-ico-widget/ico-widget/ico-widget';
import { IcoReducedHeightStandard } from 'site-modules/shared/components/dealer-ico-widget/ico-reduced-height-standard/ico-reduced-height-standard';

import './dealer.scss';

EngagementHandler.register(DealerInternalReferrerEngagementHandler);

/**
 * Add Sticky for proper positioning on the landing page
 * Several empty containers were added as well (to demonstrate scrolling)
 */
function DealerDetailsPageUI(props) {
  useEffect(() => {
    TrackingHandler.useMap(TrackingMap);
  }, []);

  const { dealerInventory, dealerInfo } = props;
  const dealerIdRaw = dealerInfo?.rooftopId;

  if (!dealerIdRaw) {
    return null;
  }

  const dealerId = dealerIdRaw.toString();
  // Populate missing params when using dealer ID
  const params = {
    ...props.params,
    dealerCity: makeDDPCityStateName(get(dealerInfo, 'address.city')),
    dealerState: makeDDPCityStateName(get(dealerInfo, 'address.stateName')),
    logicalName: dealerInfo.logicalName,
    dealerId,
  };

  const breadcrumbs = getDealerPageBreadcrumbs(dealerInfo);

  return (
    <main id="main-content" className="dealer-page">
      {!!breadcrumbs && (
        <Container>
          <Breadcrumbs className="ps-0" data={breadcrumbs} />
        </Container>
      )}
      <DealerPhotosCarouselUI dealerInfo={dealerInfo} />
      <DealerDetails
        dealerInfo={dealerInfo}
        hasShowroomPhotos={!!(dealerInfo && dealerInfo.showroomPhotos && !!dealerInfo.showroomPhotos.length)}
      />
      <IcoReducedHeightStandard dealerId={dealerId} IcoWidgetComponent={IcoWidget} />
      <hr className="mb-0 mt-2 hidden-md-down" />
      <DealerReviews dealerInfo={dealerInfo} params={params} />
      {dealerInventory && dealerInventory.models && dealerInventory.models.length > 0 && (
        <MinHeightWrapper componentType="DEALER_INVENTORY_CAROUSEL" className="mb-3">
          <DealerInventoryCarousel makeModels={dealerInventory} />
        </MinHeightWrapper>
      )}
      <DealerVideos dealerInfo={dealerInfo} />
      <DealerAboutInfoUI dealerInfo={dealerInfo} />
      <DealerSeoLinks className="mb-3" />
    </main>
  );
}

DealerDetailsPageUI.propTypes = {
  params: DEALER_PROPS.isRequired,
  dealerInventory: PropTypes.shape({
    rooftopId: PropTypes.number,
    rooftopCounters: PropTypes.shape({
      total: PropTypes.number,
      new: PropTypes.number,
      cpo: PropTypes.number,
      used: PropTypes.number,
    }),
    models: PropTypes.arrayOf(PropTypes.object),
  }),
  dealerInfo: DealerInfoEntities.DealerInfo,
};

DealerDetailsPageUI.defaultProps = {
  dealerInventory: {
    rooftopCounters: {},
    models: [],
  },
  dealerInfo: {
    showroomPhotos: [],
    name: '',
    videos: [],
  },
};

/**
 * @param {string} rootPath E.g., dealerInfo
 * @param {object} props Should contain target source value.
 *  E.g., props.params.logicalName = Abc
 * @returns {string} [rootPath].name/.id path version,
 *  based on available target props value. Empty if unavailable.
 *  E.g., 'dealerInfo.name.Abc'
 */
export function getDealerModelPath(rootPath, props) {
  const { logicalName } = props.params;
  return `${rootPath}.name.${encodeAndLowercaseDealerLogicalName(logicalName)}`;
}

export const stateToPropsConfig = {
  dealerInventory: bindToPath(props => getDealerModelPath('dealerInventory', props), DealerInventoryModel, null, false),
  dealerInfo: bindToPath(props => getDealerModelPath('dealerInfo', props), DealerInfoModel, null, false),
};

export const DealerDetailsPage = connectToModel(DealerDetailsPageUI, stateToPropsConfig);
